import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    BookOpen,
    Clock,
    Video,
    Download,
    Image,
    Wrench,
    Bookmark,
    Eye,
    TrendingUp,
    FileText
} from 'lucide-react';

const ResourceCard = ({ resource, onBookmark }) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const categoryColors = {
        'depression': 'bg-indigo-100 text-indigo-700',
        'anxiety': 'bg-purple-100 text-purple-700',
        'stress-management': 'bg-blue-100 text-blue-700',
        'suicide-prevention': 'bg-red-100 text-red-700',
        'suicide-ideation': 'bg-red-100 text-red-700',
        'self-harm': 'bg-orange-100 text-orange-700',
        'relationships': 'bg-pink-100 text-pink-700',
        'academic-performance': 'bg-green-100 text-green-700',
        'crisis-support': 'bg-red-100 text-red-700',
        'general-wellness': 'bg-teal-100 text-teal-700'
    };

    const contentTypeIcons = {
        'article': BookOpen,
        'video': Video,
        'download': Download,
        'infographic': Image,
        'tool': Wrench
    };

    const ContentIcon = contentTypeIcons[resource.contentType] || BookOpen;
    const categoryColor = categoryColors[resource.category] || 'bg-gray-100 text-gray-700';

    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleClick = () => {
        // Include user role in the navigation path
        navigate(`/${user.role}/resources/${resource.slug}`);
    };

    return (
        <div
            className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer group"
            onClick={handleClick}
        >
            {/* Thumbnail */}
            {resource.thumbnailUrl ? (
                <div className="h-48 overflow-hidden bg-gray-100">
                    <img
                        src={resource.thumbnailUrl}
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                </div>
            ) : (
                <div className="h-48 bg-gradient-to-br from-uzima-maroon-50 to-uzima-white-100 flex items-center justify-center">
                    <ContentIcon className="h-16 w-16 text-blue-300" />
                </div>
            )}

            {/* Content */}
            <div className="p-5">
                {/* Category Badge */}
                <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${categoryColor}`}>
                        {formatCategoryName(resource.category)}
                    </span>
                    <div className="flex items-center gap-2">
                        <ContentIcon className="h-4 w-4 text-gray-400" />
                        {resource.readTime && (
                            <div className="flex items-center text-xs text-gray-500">
                                <Clock className="h-3 w-3 mr-1" />
                                {resource.readTime} min
                            </div>
                        )}
                    </div>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {resource.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {resource.description}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center">
                            <Eye className="h-3 w-3 mr-1" />
                            {resource.views || 0}
                        </div>
                        {resource.difficulty && (
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                                {resource.difficulty}
                            </span>
                        )}
                        {resource.youtubeVideos?.length > 0 && (
                            <div className="flex items-center text-red-500" title={`${resource.youtubeVideos.length} video${resource.youtubeVideos.length > 1 ? 's' : ''}`}>
                                <Video className="h-3 w-3 mr-1" />
                                {resource.youtubeVideos.length}
                            </div>
                        )}
                        {resource.ebooks?.length > 0 && (
                            <div className="flex items-center text-green-600" title={`${resource.ebooks.length} e-book${resource.ebooks.length > 1 ? 's' : ''}`}>
                                <FileText className="h-3 w-3 mr-1" />
                                {resource.ebooks.length}
                            </div>
                        )}
                    </div>

                    {onBookmark && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onBookmark(resource._id);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                            title="Bookmark"
                        >
                            <Bookmark className="h-4 w-4 text-gray-600 hover:text-blue-600" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResourceCard;
